import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  className?: string;
  /** Outer rounded radius — should match the inner card radius. */
  radius?: number;
  /** Animation period in seconds (lap time of the snake). Default 6s. */
  speedSec?: number;
  /** Extra class for the inner content card. */
  innerClassName?: string;
}

/**
 * Wraps children with a 1.5px conic-gradient ring that rotates
 * continuously, producing the "snake chasing its tail" glow used to
 * draw attention to important blocks.
 *
 * Implementation: a `::before` pseudo holds the conic gradient and is
 * animated via the `@property --pb-glow-angle` registered angle. We
 * mask it with a transparent center using `padding + content
 * background-clip` so only a thin ring shows.
 */
export function GlowBorder({
  children,
  className,
  radius = 20,
  speedSec = 6,
  innerClassName,
}: Props) {
  const style = {
    '--pb-glow-radius': `${radius}px`,
    '--pb-glow-speed': `${speedSec}s`,
  } as CSSProperties;
  return (
    <div className={clsx('pb-glow', className)} style={style}>
      <div className={clsx('pb-glow-inner', innerClassName)}>{children}</div>
    </div>
  );
}
